import "./FooterForm.scss";
import { ContactsComponent_Plain } from "../../types/components/page/ContactsComponent.ts";
import { LinWithIconComponent_Plain } from "../../types/components/shared/LinWithIconComponent.ts";
import { getStrapiMedia, sendStrapiRequest } from "../../utils/utils.tsx";
import { useState, useEffect, useRef, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../Forms/Input/Input.tsx";
import {
  company_validation,
  email_validation,
  from_hidden_validation,
  message_validation,
  name_validation,
  to_hidden_validation,
} from "../../utils/inputValidations.tsx";
import Hidden from "../Forms/Hidden/Hidden.tsx";
import { OfficeComponent_Plain } from "../../types/components/shared/OfficeComponent.ts";
import { useTranslation } from "react-i18next";
import {
  company_validation_uk,
  email_validation_uk,
  from_hidden_validation_uk,
  message_validation_uk,
  name_validation_uk,
  to_hidden_validation_uk,
} from "../../utils/inputValidationsUK.tsx";
import {
  company_validation_pl,
  email_validation_pl,
  from_hidden_validation_pl,
  message_validation_pl,
  name_validation_pl,
  to_hidden_validation_pl,
} from "../../utils/inputValidationsPL.tsx";
import {
  company_validation_ro,
  email_validation_ro,
  from_hidden_validation_ro,
  message_validation_ro,
  name_validation_ro,
  to_hidden_validation_ro,
} from "../../utils/inputValidationsRO.tsx";
import {
  company_validation_hr,
  email_validation_hr,
  from_hidden_validation_hr,
  message_validation_hr,
  name_validation_hr,
  to_hidden_validation_hr,
} from "../../utils/inputValidationsHR.tsx";

const FooterForm = ({
  data,
  office,
  locale,
}: {
  data: ContactsComponent_Plain;
  office: OfficeComponent_Plain[] | null;
  locale: string;
}) => {
  const { t } = useTranslation();
  const socialItems = data.socials.map(
    (item: LinWithIconComponent_Plain, i: number) => (
      <li key={i}>
        <a href={item.link} target={item.target}>
          {item.icon ? (
            <img
              src={getStrapiMedia(item.icon.url)}
              width="50"
              alt={item.title}
            />
          ) : (
            ""
          )}
        </a>
      </li>
    ),
  );

  const emails = data.to.map((item) => ({
    name: item.name,
    email: item.email,
    officeName: null,
  }));
  let emailsAdditional: {
    name: string;
    email: string;
    officeName: string | null;
  }[] = [];
  if (office) {
    emailsAdditional = office
      .filter((item) => item.showInForm !== false)
      .map((item) => ({
        name: item.city + ", " + item.country,
        email: item.email,
        officeName: (item as { officeName?: string }).officeName || null,
      }));
  }

  const toItems = emails.map((item, i: number) => (
    <li
      key={i}
      onClick={() =>
        handleListItemClick(item.name, item.email, item.officeName)
      }
      data-email={item.email}
    >
      {item.name}
    </li>
  ));

  const methods = useForm({
    defaultValues: {
      to: emails[0]?.email || "",
      location: emails[0]?.name || "",
      officeName: emails[0]?.officeName || null,
    },
  });

  const [success, setSuccess] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const messageToRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);

  const onSubmit = methods.handleSubmit((data) => {
    sendStrapiRequest("/api/forms", data).then((res) => {
      console.log(res);
    });

    methods.reset();
    setSuccess(true);
  });

  const handleListItemClick = useCallback(
    (name: string, email: string, officeName: string | null = null) => {
      console.log(
        "Selected item - Name:",
        name,
        "Email:",
        email,
        "Office Name:",
        officeName,
      );
      methods.setValue("to", email);
      methods.setValue("location", name);
      methods.setValue("officeName", officeName);

      if (titleRef.current) {
        titleRef.current.textContent = name;
      }
      setIsDropdownOpen(false);
      if (dropdownRef.current) {
        setTimeout(() => setIsDropdownOpen(false), 0);
      }
    },
    [methods, titleRef, setIsDropdownOpen, dropdownRef],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      console.log("toItems length:", toItems.length);
      if (toItems.length === 1 && messageToRef.current) {
        messageToRef.current.classList.add("one-email");
        const singleItem = emails[0];
        if (singleItem) {
          handleListItemClick(
            singleItem.name,
            singleItem.email,
            singleItem.officeName,
          );
        }
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [toItems.length, emails, handleListItemClick]);

  useEffect(() => {
    isMounted.current = true;
    console.log("FooterForm mounted");

    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      isMounted.current = false;
      console.log("FooterForm unmounted");
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  return (
    <section className="box-form" id="form">
      <div className="wrapper">
        <div className="form__left">
          <h2>
            {data.title1} <span>{data.title2}</span> {data.title3}
          </h2>
          <div className="form__social">
            <div className="form__social-title">{data.socialsTitle}</div>
            <div className="social-list">
              <ul>{socialItems}</ul>
            </div>
          </div>
        </div>
        <div className="form__right">
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => e.preventDefault()}
              noValidate
              autoComplete="off"
              className="form"
            >
              <div className="message-to" ref={messageToRef}>
                <div className="message-to__text">{t("Message to")}:</div>
                <div
                  className={`message-to__select ${
                    isDropdownOpen ? "active-select" : ""
                  }`}
                  ref={selectRef}
                >
                  <div
                    className="message-to__select-title"
                    ref={titleRef}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {methods.getValues("to")
                      ? [...emails, ...emailsAdditional].find(
                          (item) => item.email === methods.getValues("to"),
                        )?.name
                      : emails[0]?.name || t("Select an option")}{" "}
                  </div>
                  <div className="message-to__select-dropdown">
                    <ul ref={dropdownRef}>{toItems}</ul>
                  </div>
                </div>
              </div>
              {locale == "uk" ? (
                <>
                  <Hidden {...from_hidden_validation_uk} />
                  <Hidden {...to_hidden_validation_uk} />
                  <Input {...name_validation_uk} />
                  <Input {...email_validation_uk} />
                  <Input {...company_validation_uk} />
                  <Input {...message_validation_uk} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              ) : locale == "pl" ? (
                <>
                  <Hidden {...from_hidden_validation_pl} />
                  <Hidden {...to_hidden_validation_pl} />
                  <Input {...name_validation_pl} />
                  <Input {...email_validation_pl} />
                  <Input {...company_validation_pl} />
                  <Input {...message_validation_pl} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              ) : locale == "ro" ? (
                <>
                  <Hidden {...from_hidden_validation_ro} />
                  <Hidden {...to_hidden_validation_ro} />
                  <Input {...name_validation_ro} />
                  <Input {...email_validation_ro} />
                  <Input {...company_validation_ro} />
                  <Input {...message_validation_ro} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              ) : locale == "hr" ? (
                <>
                  <Hidden {...from_hidden_validation_hr} />
                  <Hidden {...to_hidden_validation_hr} />
                  <Input {...name_validation_hr} />
                  <Input {...email_validation_hr} />
                  <Input {...company_validation_hr} />
                  <Input {...message_validation_hr} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              ) : (
                <>
                  <Hidden {...from_hidden_validation} />
                  <Hidden {...to_hidden_validation} />
                  <Input {...name_validation} />
                  <Input {...email_validation} />
                  <Input {...company_validation} />
                  <Input {...message_validation} />
                  <input type="hidden" {...methods.register("location")} />
                  <input type="hidden" {...methods.register("officeName")} />
                </>
              )}
              {success && (
                <p className="font-semibold text-green-500 mb-5 flex items-center gap-1">
                  {t("Form has been submitted successfully")}
                </p>
              )}
              <button onClick={onSubmit} className="btn-black">
                {t("Submit your message")}
              </button>
            </form>
          </FormProvider>
        </div>
        <div className="form__social show-tab">
          <div className="form__social-title">{data.socialsTitle}</div>
          <div className="social-list">
            <ul>{socialItems}</ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FooterForm;
